import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { FeaturedMealsCard, Loader, Pagination } from '../components/utils';
import { Section, Fotter, Header } from '../components/layout';
import { useStateContext } from '../context/ContextProvider';
import { getAllCategorys } from '../redux/actions/category';
import MealsFilter from '../components/MealsFilter';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGrid, FiList } from 'react-icons/fi';
import { getFilteredFeaturedMealsWithSort } from '../redux/actions/featuredMeals';

function AllFeaturedMeals() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentDir } = useStateContext();
  
  // States
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(12);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    priceMin: '',
    priceMax: '',
    sortBy: 'default'
  });

  // Redux State
  const { isLoading, FilltermealsWithSort, FilterpaginationWithSort } = useSelector((state) => state.FeaturedMeals);
  const { AllCategorys } = useSelector((state) => state.Categorys);

  // Fetch data when filters or pagination change
  useEffect(() => {
    dispatch(getFilteredFeaturedMealsWithSort(page, limit, filters));
  }, [page, limit, filters, dispatch]);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(getAllCategorys());
  }, [dispatch]);

  const totalPages = FilterpaginationWithSort?.totalPages || 0;

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  // Sort meals locally if needed (as fallback)
  const getSortedMeals = () => {
    if (!FilltermealsWithSort) return [];
    
    let sorted = [...FilltermealsWithSort];
    
    switch(filters.sortBy) {
      case 'price_asc':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price_desc':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      default:
        // default sorting by creation date
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    
    return sorted;
  };

  const displayedMeals = getSortedMeals();

  return (
    <>
      <Header active={3} DropActive={3} />
      
      {/* Hero Section */}
      <motion.div 
        className="relative bg-gradient-to-r from-color-1/10 to-color-2/10 dark:from-color-1/5 dark:to-color-2/5 pt-24 pb-16 mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h1 
       className="text-4xl md:text-5xl lg:text-6xl  font-kufi font-bold mb-4 bg-gradient-to-r from-color-1 to-color-2 bg-clip-text text-transparent pb-6"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {t('all Offers')}
          </motion.h1>
          <motion.p 
            className="body-1 text-n-6 dark:text-n-3 max-w-2xl mx-auto"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {t("Discover our special offers - great value, amazing taste, unforgettable experience.")}
          </motion.p>
        </div>
      </motion.div>

      {isLoading ? (
        <Loader />
      ) : (
        <Section>
          <div className="container mx-auto px-4 py-12 lg:py-20">
            
            {/* Filters Component */}
            <MealsFilter 
              onFilterChange={handleFilterChange}
              categories={AllCategorys}
              initialFilters={filters}
            />

            {/* View Toggle & Stats */}
            <motion.div 
              className="flex flex-wrap justify-between items-center gap-4 mb-12"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-3">
                <span className="text-n-5 dark:text-n-3 body-2">
                  {t('showing')} <span className="font-bold text-color-1">{displayedMeals?.length || 0}</span> {t('items')}
                </span>
                <div className="h-6 w-px bg-gray-300 dark:bg-n-6" />
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      viewMode === 'grid' 
                        ? 'bg-color-1 text-white shadow-lg' 
                        : 'bg-n-2 dark:bg-n-7 text-n-6 dark:text-n-3 hover:bg-n-3 dark:hover:bg-n-6'
                    }`}
                    aria-label="Grid view"
                  >
                    <FiGrid size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      viewMode === 'list' 
                        ? 'bg-color-1 text-white shadow-lg' 
                        : 'bg-n-2 dark:bg-n-7 text-n-6 dark:text-n-3 hover:bg-n-3 dark:hover:bg-n-6'
                    }`}
                    aria-label="List view"
                  >
                    <FiList size={18} />
                  </button>
                </div>
              </div>
              
              <div className="text-sm text-n-5 dark:text-n-4">
                {FilltermealsWithSort?.totalItems && (
                  <span>{t('total')}: {FilltermealsWithSort.totalItems} {t('meals')}</span>
                )}
              </div>
            </motion.div>

            {/* Meals Grid/List */}
            <AnimatePresence mode="wait">
              <motion.div
                key={viewMode + filters.sortBy}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0 }}
                className={`${
                  viewMode === 'grid' 
                    ? 'grid lg:grid-cols-3 md:grid-cols-2 gap-8' 
                    : 'flex flex-col gap-6'
                }`}
              >
                {displayedMeals?.map((meal, index) => (
                  <motion.div 
                    key={meal._id} 
                    variants={itemVariants}
                    layout
                  >
                    <FeaturedMealsCard meals={meal} viewMode={viewMode} />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Empty State */}
            {(!displayedMeals || displayedMeals.length === 0) && (
              <motion.div 
                className="text-center py-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="text-6xl mb-4">🍽️</div>
                <h3 className="h4 text-n-6 dark:text-n-3 mb-2">{t('no dishes found')}</h3>
                <p className="body-2 text-n-5 dark:text-n-4">{t('try adjusting your filters')}</p>
                <button
                  onClick={() => handleFilterChange({
                    search: '',
                    categoryId: '',
                    priceMin: '',
                    priceMax: '',
                    sortBy: 'default'
                  })}
                  className="mt-6 px-6 py-2 bg-color-1 text-white rounded-lg hover:bg-color-1/90 transition-all"
                >
                  {t('clear all filters')}
                </button>
              </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div 
                className="mt-16"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  setPage={setPage}
                />
              </motion.div>
            )}
          </div>
        </Section>
      )}
      <Fotter />
    </>
  );
}

export default AllFeaturedMeals;